import { FC, useMemo, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import { EditorialFacebookPostResource } from '@/types/resources';
import { EditorialFacebookPostType } from '@/utils/enums';

import { Checkbox, CheckboxHolder, CheckboxLabel } from './Table';

const Dialog = styled(StyledDialog)`
  width: 600px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

const Grid = styled.div`
  display: flex;
  gap: 40px;
`;

const postTypes = [
  {
    value: EditorialFacebookPostType.IMAGE,
    label: 'Image',
  },
  {
    value: EditorialFacebookPostType.LINK,
    label: 'Link',
  },
  {
    value: EditorialFacebookPostType.PLACEHOLDER,
    label: 'Placeholder',
  },
  {
    value: EditorialFacebookPostType.REEL,
    label: 'Reel',
  },
  {
    value: EditorialFacebookPostType.STORY,
    label: 'Story',
  },
  {
    value: EditorialFacebookPostType.TEXT,
    label: 'Text',
  },
  {
    value: EditorialFacebookPostType.VIDEO,
    label: 'Video',
  },
];

const postTypeMap = new Map(postTypes.map((pt) => [pt.value, pt.label]));

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface Props {
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
  onClose: VoidFunction;
  posts: EditorialFacebookPostResource[];
}

const BulkSelectionModal: FC<Props> = ({ onClose, posts, setSelectedRows }) => {
  const [selectedPostTypes, setSelectedPostTypes] = useState<number[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);

  const data = useMemo(() => {
    const typesCounts = new Map();

    for (const obj of posts) {
      const type = obj.type;
      typesCounts.set(type, (typesCounts.get(type) || 0) + 1);
    }

    const postTypes = Array.from(typesCounts, ([type, count]) => ({
      type,
      count,
    }));

    const ownerCounts = new Map();

    for (const obj of posts) {
      const owner = obj.owner;
      ownerCounts.set(owner, (ownerCounts.get(owner) || 0) + 1);
    }

    const owners = Array.from(ownerCounts, ([owner, count]) => ({
      owner,
      count,
    }));

    return {
      postTypes,
      owners,
    } as {
      postTypes: { type: number; count: number }[];
      owners: { owner: string; count: number }[];
    };
  }, [posts]);

  const selectedPosts = useMemo(() => {
    if (!selectedOwners.length && !selectedPostTypes.length) {
      return [];
    }

    return posts.filter((post) => {
      return (
        selectedPostTypes.includes(post.type) ||
        selectedOwners.includes(post.owner)
      );
    });
  }, [posts, selectedOwners, selectedPostTypes]);

  const handleSelection = () => {
    setSelectedRows(new Set(selectedPosts.map((p) => p.id)));
    onClose();
  };

  const handleSelectAll = () => {
    setSelectedRows(new Set(posts.map((p) => p.id)));
    onClose();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Select posts</Heading>
        <Grid>
          <List>
            <Text $type="captionSemibold">By post types:</Text>
            {data.postTypes.map(({ type, count }, i) => (
              <Row key={type}>
                <CheckboxHolder>
                  <Checkbox
                    id={`checkbox-post-types-${i}`}
                    name="products"
                    type="checkbox"
                    checked={selectedPostTypes.includes(type)}
                    onChange={() =>
                      setSelectedPostTypes((prev) =>
                        prev.includes(type)
                          ? prev.filter((t) => t !== type)
                          : [...prev, type],
                      )
                    }
                  />
                  <CheckboxLabel htmlFor={`checkbox-post-types-${i}`} />
                </CheckboxHolder>
                <Text>
                  {postTypeMap.get(type)} ({count})
                </Text>
              </Row>
            ))}
          </List>
          <List>
            <Text $type="captionSemibold">By owners:</Text>
            {data.owners.map(({ owner, count }, i) => (
              <Row key={owner}>
                <CheckboxHolder>
                  <Checkbox
                    id={`checkbox-owners-${i}`}
                    name="products"
                    type="checkbox"
                    checked={selectedOwners.includes(owner)}
                    onChange={() =>
                      setSelectedOwners((prev) =>
                        prev.includes(owner)
                          ? prev.filter((o) => o !== owner)
                          : [...prev, owner],
                      )
                    }
                  />
                  <CheckboxLabel htmlFor={`checkbox-owners-${i}`} />
                </CheckboxHolder>
                <Text>
                  {owner} ({count})
                </Text>
              </Row>
            ))}
          </List>
        </Grid>

        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton type="button" color="primary" onClick={handleSelectAll}>
            Select All
          </FooterButton>
          <FooterButton
            type="button"
            color="primary"
            onClick={handleSelection}
            disabled={!selectedPosts.length}
          >
            Select{selectedPosts.length ? `(${selectedPosts.length})` : ''}
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default BulkSelectionModal;
